import { useState, type FC, useCallback, useEffect, useRef } from 'react'
import { Modal } from 'antd'
import type { IPatient } from 'features/Patients/interfaces/IPatient'
import { Table } from 'common/components/Table/Table'
import type { TableParamsType } from 'common/interfaces/ITable'
import { DEFAULT_TABLE_PARAMS_WITH_SEARCH } from 'common/constants/defaultTableParamsConstants'
import { type ITriggerPagRequest } from 'common/interfaces/IRequestResponse'
import { useLazyFetchPatientsQuery } from 'features/Patients/state/api/patientApi'
import { Row, Col, Typography } from 'antd'
import styles from './patientSearchModal.module.scss'
import { Input } from 'common/components/Input/Input'
import { Button } from 'common/components/Button/Button'
import { patientsTableColumns } from './columns'
import { BUTTON_CONSTANTS } from 'common/constants/buttonConstants'
import { BUTTON_MODIFIER } from 'common/constants/buttonConstants'

const { Text } = Typography

type Props = {
  isOpen: boolean
  handleConfirm?: (patient: IPatient) => void
  onClose: () => void
  onSelectPatient?: (patient: IPatient) => void
}

const PatientsTableParams = {
  ...DEFAULT_TABLE_PARAMS_WITH_SEARCH,
  pageSize: 10,
}

export const PatientSearchModal: FC<Props> = ({
  isOpen,
  onClose,
  onSelectPatient,
  handleConfirm,
}) => {
  const [tableParams, setTableParams] = useState<TableParamsType>(PatientsTableParams)
  const [searchValue, setSearchValue] = useState<string>('')
  const [selectedPatient, setSelectedPatient] = useState<IPatient | null>(null)
  const searchInputRef = useRef<any>(null)

  const [
    fetchPatientsData,
    { data: PatientsData, isFetching: isLoadingPatientsData },
  ]: ITriggerPagRequest<IPatient[]> = useLazyFetchPatientsQuery()

  const handleUpdateRow = (patient: IPatient) => {
    setSelectedPatient(patient)
    onSelectPatient?.(patient)
    if (!handleConfirm) onClose()
  }

  const handleSearch = () => {
    if (searchValue.length >= 2) {
      setTableParams({
        ...tableParams,
        debouncedValue: searchValue,
      })
    }
  }

  const focusSearchInput = useCallback(() => {
    requestAnimationFrame(() => {
      searchInputRef.current?.focus?.()
    })
  }, [])

  useEffect(() => {
    if (tableParams.debouncedValue?.length >= 2) {
      fetchPatientsData({
        ...tableParams,
        search: tableParams.debouncedValue,
      })
    }
  }, [tableParams.pageSize, tableParams.current, tableParams.debouncedValue, tableParams.sorters])

  useEffect(() => {
    if (Number.isInteger(PatientsData?.total_items))
      setTableParams({ ...tableParams, total: PatientsData.total_items })
  }, [PatientsData])

  return (
    <Modal
      classNames={{
        content: styles.content,
      }}
      afterOpenChange={(open) => {
        if (open) {
          focusSearchInput()
        }
      }}
      destroyOnHidden
      title='Patient Search'
      open={isOpen}
      onCancel={onClose}
      width={800}
      footer={null}>
      <>
        <div className={styles.searchContainer}>
          <Row gutter={[16, 8]} align='middle' justify={{ xs: 'space-between', sm: 'start' }}>
            <Col>
              <Text className={styles.searchLabel} strong>
                Search for:
              </Text>
            </Col>
            <Col flex='auto' xs={{ order: 3 }} sm={{ order: 2 }}>
              <Input
                ref={searchInputRef}
                allowClear
                name='search'
                placeholder='First name, last name, phone number'
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSearch()
                  }
                }}
              />
            </Col>
            <Col xs={{ order: 2 }} sm={{ order: 3 }}>
              <Button onClick={handleSearch} disabled={searchValue.trim().length < 2}>
                Search
              </Button>
            </Col>
          </Row>
        </div>

        {PatientsData?.items && (
          <div className={styles.recordsFound}>
            <Text strong>{PatientsData.total_items} records found.</Text>
          </div>
        )}

        <Table
          onClickRow={handleUpdateRow}
          isLoading={isLoadingPatientsData}
          itemKey='id'
          scroll={{ x: 'max-content' }}
          tableParams={tableParams}
          dataSource={PatientsData?.items}
          setTableParams={setTableParams}
          columns={patientsTableColumns}
          haveActions={false}
          rowClassName={(record: IPatient) =>
            handleConfirm && selectedPatient?.id === record.id ? 'ant-table-row-selected' : ''
          }
        />

        {handleConfirm && (
          <Row gutter={[16, 8]} align='middle' justify='end' className={styles.confirmContainer}>
            <Col>
              <Button
                modifier={BUTTON_MODIFIER.DEFAULT}
                onClick={() => {
                  onClose()
                }}>
                {BUTTON_CONSTANTS.CANCEL}
              </Button>
            </Col>
            <Col>
              <Button onClick={() => handleConfirm(selectedPatient)}>
                {BUTTON_CONSTANTS.SUBMIT}
              </Button>
            </Col>
          </Row>
        )}
      </>
    </Modal>
  )
}
